import React, { useEffect } from 'react';
import { GameViewModel } from '../../../resources/js/typings/viewModels/gameViewModel';
import { GameContextProvider } from '../../../resources/js/contexts/useGame';
import { GetStaticPaths, GetStaticProps } from 'next';
import getBuildsZonePageViewModels, {
  BuildsZonePageViewModel,
} from '../../helpers/getBuildsZonePageViewModels';
import getBuildsZonePageSlugs from '../../helpers/getBuildsZonePageSlugs';
import configureSentryScope from '../../helpers/configureSentryScope';
import VerticalContent from '../../../resources/js/components/VerticalContent/VerticalContent';
import Seo from '../../components/Seo/Seo';
import Heading from '../../../resources/js/components/Heading/Heading';
import Body from '../../../resources/js/components/Body/Body';
import PageBackground from '../../components/PageBackground/PageBackground';
import styles from './builds.module.scss';
import EnhancedMarkdown from '../../../resources/js/components/EnhancedMarkdown/EnhancedMarkdown';
import { ArchonHeaderViewModel } from '../../components/ArchonHeader/ArchonHeader';
import { ArchonFooterViewModel } from '../../components/ArchonFooter/ArchonFooter';
import ArchonLayout, {
  Container,
} from '../../layouts/ArchonLayout/ArchonLayout';
import BuildsTierListSection from '../../../resources/js/components/BuildsTierListSection/BuildsTierListSection';
import BuildsRankingsSection from '../../../resources/js/components/BuildsRankingsSection/BuildsRankingsSection';
import LinkWrapper from '../../components/LinkWrapper/LinkWrapper';
import BuildsMetadataSummary from '@resources/js/components/BuildsMetadataSummary/BuildsMetadataSummary';
import LazyLoad from 'react-lazyload';
import BuildsZonePageFilter from '../../components/ZonePageFilter/BuildsZonePageFilter';
import { sendEvent } from '@resources/js/helpers/analytics';
import unique from '@resources/js/helpers/unique';
import config from '../../config';
import BuildsSubcreationMigrationSection from '@resources/js/components/BuildsSubcreationMigrationSection/BuildsSubcreationMigrationSection';
import BuildsAdvancedSettings from '@resources/js/components/BuildsAdvancedSettings/BuildsAdvancedSettings';
import { initRequest } from '../../helpers/rpgLogsApiRequestMetadata';
import { V3CompactTheme } from '@resources/js/contexts/useTheme';

type BuildsZonePageProps = {
  game: GameViewModel;
  header: ArchonHeaderViewModel;
  footer: ArchonFooterViewModel;
  page: BuildsZonePageViewModel;
};

export function BuildsZonePage({
  game,
  header,
  footer,
  page,
}: BuildsZonePageProps): JSX.Element {
  useEffect(() => {
    sendLoadEvent({
      gameSlug: game.archonGameSlug,
      roleSlug: page.selectedRole,
      zoneTypeSlug: page.selectedZoneType,
      difficultySlug: page.selectedDifficulty,
      encounterSlug: page.selectedEncounter,
      affixesSlug: page.selectedAffixes,
    });
  }, [game, page]);

  return (
    <GameContextProvider game={game}>
      <ArchonLayout
        header={header}
        breadcrumbs={page.breadcrumbs}
        footer={footer}
        theme={V3CompactTheme}
      >
        <Seo {...page.seo} game={game} />
        <PageBackground
          imageUrl={page.zoneBackgroundUrl}
          width={1218}
          height={564}
          withBreadcrumbs={page.breadcrumbs.length > 0}
        />
        <Container shouldShowAdsIfEnabled={true}>
          <Body>
            <VerticalContent
              className={`builds-page__content enhanced-markdown ${styles.topElement}`}
              gap={'larger'}
            >
              <BuildsZonePageFilter {...page} />
              <section className={'no-top-margin-on-compact'}>
                <Heading level={1} className={'hide-on-compact'}>
                  <EnhancedMarkdown markdown={page.title} wrapped={false} />
                </Heading>
                <Body className={`${styles.headerDescription} hide-on-compact`}>
                  <EnhancedMarkdown
                    markdown={page.description}
                    wrapped={false}
                  />
                </Body>
                <div className={'builds--page-subtext'}>
                  <BuildsMetadataSummary {...page} />
                </div>
              </section>
              <section>
                <LazyLoad height={500}>
                  <BuildsTierListSection
                    {...page.specTierListSection}
                    LinkWrapper={LinkWrapper}
                  />
                </LazyLoad>
              </section>
              <section>
                <LazyLoad height={500}>
                  <BuildsRankingsSection
                    {...page.specRankingsSection}
                    LinkWrapper={LinkWrapper}
                    limit={7}
                  />
                </LazyLoad>
              </section>
              {page.heroSpecRankingsSection ? (
                <section>
                  <LazyLoad height={500}>
                    <BuildsRankingsSection
                      {...page.heroSpecRankingsSection}
                      LinkWrapper={LinkWrapper}
                      limit={15}
                    />
                  </LazyLoad>
                </section>
              ) : null}
              {page.encounterTierListSection ? (
                <section>
                  <LazyLoad height={500}>
                    <BuildsTierListSection
                      {...page.encounterTierListSection}
                      LinkWrapper={LinkWrapper}
                      showMetricOptionsAndDescription={false}
                    />
                  </LazyLoad>
                </section>
              ) : null}
              {page.encounterRankingsSection ? (
                <section>
                  <LazyLoad height={500}>
                    <BuildsRankingsSection
                      {...page.encounterRankingsSection}
                      LinkWrapper={LinkWrapper}
                    />
                  </LazyLoad>
                </section>
              ) : null}
              {page.subcreationMigrationSection ? (
                <section>
                  <LazyLoad>
                    <BuildsSubcreationMigrationSection
                      title={page.subcreationMigrationSection.title}
                      description={page.subcreationMigrationSection.description}
                    />
                  </LazyLoad>
                </section>
              ) : null}
              <section>
                <BuildsAdvancedSettings {...page.advancedSettings} />
              </section>
            </VerticalContent>
          </Body>
        </Container>
      </ArchonLayout>
    </GameContextProvider>
  );
}

export const getZonePageStaticPaths: GetStaticPaths = async () => {
  const allSlugs = (await getBuildsZonePageSlugs()).filter(
    (slugs) => !slugs.affixesSlug
  );

  const paths = [];

  const gameSlugs = allSlugs
    .map((slugs) => slugs.gameSlug)
    .filter(unique)
    .filter((x) => x !== 'eso');
  for (const gameSlug of gameSlugs) {
    const slugsForGame = allSlugs.filter(
      (slugs) => slugs.gameSlug === gameSlug
    );
    const zoneTypeSlugs = slugsForGame
      .map((slugs) => slugs.zoneTypeSlug)
      .filter(unique);

    for (const zoneTypeSlug of zoneTypeSlugs) {
      const slugsForZoneType = slugsForGame.filter(
        (slugs) => slugs.zoneTypeSlug === zoneTypeSlug
      );

      const difficultySlugs = slugsForZoneType
        .map((slugs) => slugs.difficultySlug)
        .filter(unique);

      // For each difficulty of each zone of each game...
      for (let i = 0; i < difficultySlugs.length; i++) {
        const difficultySlug = difficultySlugs[i];
        const slugsForDifficulty = slugsForZoneType.filter(
          (slugs) => slugs.difficultySlug === difficultySlug
        );

        const rankingsSlugs = slugsForDifficulty
          .map((slugs) => slugs.rankingsSlug)
          .filter(unique);
        const rankingsSlugsToPreBuild = config.isProduction
          ? rankingsSlugs
          : [rankingsSlugs[0]];

        const encounterSlugs = slugsForDifficulty
          .map((slugs) => slugs.encounterSlug)
          .filter(unique);
        const encounterSlugsToPreBuild =
          i === 0
            ? config.isProduction
              ? // For the default difficulty, either pre-build all encounters
                encounterSlugs
              : // Or nothing if not production/beta
                []
            : config.isProduction
            ? i === 1
              ? [
                  // For the second difficulty, pre-build the overall encounter, the first boss
                  ...encounterSlugs.slice(0, 2),
                  // and the last boss
                  ...encounterSlugs.slice(-1),
                ].filter(unique)
              : // Or nothing if not production/beta
                []
            : [];

        const slugs = slugsForDifficulty.filter(
          (slugs) =>
            rankingsSlugsToPreBuild.includes(slugs.rankingsSlug) &&
            encounterSlugsToPreBuild.includes(slugs.encounterSlug)
        );

        if (config.isCI) {
          console.log(
            `Pre-building ${slugs.length} zone pages for ${gameSlug} ${zoneTypeSlug} ${difficultySlug}`
          );
        }

        for (const slugsToPreBuild of slugs) {
          paths.push({
            params: {
              gameSlug: slugsToPreBuild.gameSlug,
              rankingsSlug: slugsToPreBuild.rankingsSlug,
              zoneTypeSlug: slugsToPreBuild.zoneTypeSlug,
              difficultySlug: slugsToPreBuild.difficultySlug,
              encounterSlug: slugsToPreBuild.encounterSlug,
            },
          });
        }
      }
    }
  }

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getZonePageStaticPathsWithAffixes: GetStaticPaths = async () => {
  const allSlugs = (await getBuildsZonePageSlugs()).filter(
    (slugs) => slugs.affixesSlug
  );

  const paths = [];

  const gameSlugs = allSlugs
    .map((slugs) => slugs.gameSlug)
    .filter(unique)
    .filter((x) => x !== 'eso');
  for (const gameSlug of gameSlugs) {
    const slugsForGame = allSlugs.filter(
      (slugs) => slugs.gameSlug === gameSlug
    );
    const zoneTypeSlugs = slugsForGame
      .map((slugs) => slugs.zoneTypeSlug)
      .filter(unique);

    for (const zoneTypeSlug of zoneTypeSlugs) {
      const slugsForZoneType = slugsForGame.filter(
        (slugs) => slugs.zoneTypeSlug === zoneTypeSlug
      );

      const difficultySlugs = slugsForZoneType
        .map((slugs) => slugs.difficultySlug)
        .filter(unique)
        .filter((x) => x !== 'high-keys');

      // For each difficulty of each zone of each game...
      for (let i = 0; i < difficultySlugs.length; i++) {
        const difficultySlug = difficultySlugs[i];
        const slugsForDifficulty = slugsForZoneType.filter(
          (slugs) => slugs.difficultySlug === difficultySlug
        );

        const rankingsSlugs = slugsForDifficulty
          .map((slugs) => slugs.rankingsSlug)
          .filter(unique);
        const rankingsSlugsToPreBuild = config.isProduction
          ? rankingsSlugs
          : [rankingsSlugs[0]];

        const encounterSlugs = slugsForDifficulty
          .map((slugs) => slugs.encounterSlug)
          .filter(unique);

        // For zones with affixes, only pre-build the overall encounter, and only if production
        const encounterSlugsToPreBuild =
          i === 0 && config.isProduction ? [encounterSlugs[0]] : [];

        const affixesSlugs = slugsForDifficulty
          .map((slugs) => slugs.affixesSlug)
          .filter(unique);

        const affixesSlugsToPreBuild = config.isProduction
          ? affixesSlugs.slice(0, 3)
          : [affixesSlugs[0]];

        const slugs = slugsForDifficulty.filter(
          (slugs) =>
            rankingsSlugsToPreBuild.includes(slugs.rankingsSlug) &&
            encounterSlugsToPreBuild.includes(slugs.encounterSlug) &&
            affixesSlugsToPreBuild.includes(slugs.affixesSlug)
        );

        if (config.isCI) {
          console.log(
            `Pre-building ${slugs.length} zone pages for ${gameSlug} ${zoneTypeSlug} ${difficultySlug}`
          );
        }

        for (const slugsToPreBuild of slugs) {
          paths.push({
            params: {
              gameSlug: slugsToPreBuild.gameSlug,
              rankingsSlug: slugsToPreBuild.rankingsSlug,
              zoneTypeSlug: slugsToPreBuild.zoneTypeSlug,
              difficultySlug: slugsToPreBuild.difficultySlug,
              encounterSlug: slugsToPreBuild.encounterSlug,
              affixesSlug: slugsToPreBuild.affixesSlug as string,
            },
          });
        }
      }
    }
  }

  return {
    paths,
    fallback: 'blocking',
  };
};

const thirteenHours = 13 * 60 * 60;
export const getZonePageStaticProps: GetStaticProps = async ({ params }) => {
  configureSentryScope(params);

  if (
    !params ||
    !params.gameSlug ||
    !params.rankingsSlug ||
    !params.zoneTypeSlug ||
    !params.difficultySlug ||
    !params.encounterSlug
  ) {
    return {
      notFound: true,
    };
  }

  const slugs = {
    gameSlug: params.gameSlug as string,
    rankingsSlug: params.rankingsSlug as string,
    zoneTypeSlug: params.zoneTypeSlug as string,
    difficultySlug: params.difficultySlug as string,
    encounterSlug: params.encounterSlug as string,
    affixesSlug: (params.affixesSlug as string) ?? null,
  };

  if (slugs.gameSlug === 'classic-wrath') {
    return {
      redirect: {
        statusCode: 307,
        destination: `/classic-cata/tier-list/${params.rankingsSlug}/raid/${
          params.difficultySlug
        }/all-bosses${slugs.affixesSlug ? `/${slugs.affixesSlug}` : ''}`,
      },
    };
  }

  if (
    slugs.affixesSlug === 'all-affixes' ||
    slugs.affixesSlug === 'last-week'
  ) {
    return {
      redirect: {
        statusCode: 307,
        destination: './this-week',
      },
    };
  }

  if (slugs.difficultySlug === '20') {
    return {
      redirect: {
        statusCode: 307,
        destination: `/${params.gameSlug}/tier-list/${params.rankingsSlug}/${
          params.zoneTypeSlug
        }/10/${params.encounterSlug}${
          slugs.affixesSlug ? `/${slugs.affixesSlug}` : ''
        }`,
      },
    };
  }

  initRequest({
    route: params.affixesSlug
      ? '/[gameSlug]/tier-list/[rankingsSlug]/[zoneTypeSlug]/[difficultySlug]/[encounterSlug]/[affixesSlug]'
      : '/[gameSlug]/tier-list/[rankingsSlug]/[zoneTypeSlug]/[difficultySlug]/[encounterSlug]',
    path: `/${params.gameSlug}/tier-list/${params.rankingsSlug}/${
      params.zoneTypeSlug
    }/${params.difficultySlug}/${params.encounterSlug}${
      params.affixesSlug ? `/${params.affixesSlug}` : ''
    }`,
  });

  const translationKeys = ['global.show-more', 'global.show-less'];

  const pageViewModels = await getBuildsZonePageViewModels(
    slugs,
    translationKeys
  );

  // handle the high-keys bracket not being present sometimes
  if (!pageViewModels.buildsZonePage && slugs.difficultySlug === 'high-keys') {
    return {
      redirect: {
        statusCode: 307,
        destination: `/${params.gameSlug}/tier-list/${params.rankingsSlug}/${
          params.zoneTypeSlug
        }/10/${params.encounterSlug}${
          slugs.affixesSlug ? `/${slugs.affixesSlug}` : ''
        }`,
      },
    };
  }

  if (!pageViewModels.buildsZonePage) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      googleAnalytics: pageViewModels.googleAnalytics,
      adPlacements: pageViewModels.adPlacements,
      playwireAds: pageViewModels.playwireAds,
      game: pageViewModels.game,
      translations: pageViewModels.translations,
      header: pageViewModels.header,
      footer: pageViewModels.footer,
      page: pageViewModels.buildsZonePage,
    },
    revalidate: thirteenHours,
  };
};

function sendLoadEvent(parameters: {
  gameSlug: string | null;
  zoneTypeSlug: string;
  roleSlug: string;
  difficultySlug: string;
  encounterSlug: string;
  affixesSlug: string | null;
}) {
  sendEvent({
    analyticsComponentName: 'BuildsZonePage',
    eventName: 'Load',
    isInteraction: false,
    extraParameters: {
      game: parameters.gameSlug,
      filter_1: parameters.roleSlug,
      zone_id: parameters.zoneTypeSlug,
      difficulty: parameters.difficultySlug,
      encounter_id: parameters.encounterSlug,
      filter_3: parameters.affixesSlug,
    },
  });
}
